/**
 * Wire-контракт аналитики обращений (EPIC-22).
 * Соответствует backend schemas в spro/api_entities/operator_analytics.
 */

export enum OperatorAnalyticsGranularityServer {
  hour = 'hour',
  day = 'day'
}

export enum OperatorAnalyticsChannelGroupByServer {
  kind = 'kind',
  channel = 'channel'
}

export enum OperatorAnalyticsErrorCode {
  notReady = 'operator_analytics_not_ready',
  cursorExpired = 'operator_analytics_cursor_expired',
  invalidPeriod = 'operator_analytics_invalid_period'
}

export type OperatorAnalyticsPeriodQuery = {
  start_datetime: string;
  end_datetime: string;
};

export type OperatorAnalyticsChannelsQuery = OperatorAnalyticsPeriodQuery & {
  channel_id?: string[];
  group_by: OperatorAnalyticsChannelGroupByServer;
};

export type OperatorAnalyticsTagsQuery = OperatorAnalyticsPeriodQuery & {
  tag_id?: string[];
};

export type OperatorAnalyticsQualityQuery = OperatorAnalyticsPeriodQuery & {
  rating?: number;
  cursor?: string;
  limit?: number;
};

/** Границы покрытия; backend отдаёт пустой объект, пока покрытие не рассчитано. */
export type OperatorAnalyticsCoverageServer = {
  assignments_from?: string | null;
  assignments_estimated_from?: string | null;
  first_response_exact_from?: string | null;
  first_response_estimated_from?: string | null;
  sla_from?: string | null;
};

export type OperatorAnalyticsMetaServer = {
  data_as_of: string;
  projection_lag_seconds: number;
};

export type OperatorAnalyticsMetaWithCoverageServer =
  OperatorAnalyticsMetaServer & {
    coverage: OperatorAnalyticsCoverageServer;
  };

export type OperatorAnalyticsCountPointServer = {
  at: string;
  value: number;
};

export type OperatorAnalyticsNullablePointServer = {
  at: string;
  value: number | null;
};

export type OperatorAnalyticsCountMetricServer = {
  total: number;
  points: OperatorAnalyticsCountPointServer[];
};

export type OperatorAnalyticsNullableMetricServer = {
  total: number | null;
  points: OperatorAnalyticsNullablePointServer[];
};

export type OperatorAnalyticsOverviewServer = {
  granularity: OperatorAnalyticsGranularityServer;
  metrics: {
    new_tickets: OperatorAnalyticsCountMetricServer;
    closed_tickets: OperatorAnalyticsCountMetricServer;
    first_response_seconds: OperatorAnalyticsNullableMetricServer;
    sla_violation_percent: OperatorAnalyticsNullableMetricServer;
  };
  sla_enabled: boolean | null;
  meta: OperatorAnalyticsMetaWithCoverageServer;
};

export type OperatorAnalyticsOperatorServer = {
  manager_id: string;
  name: string;
  deleted: boolean;
  assigned_tickets: number;
  closed_tickets: number;
  first_response_seconds: number | null;
  sla_violation_percent: number | null;
  csat_average: number | null;
};

export type OperatorAnalyticsOperatorsServer = {
  operators: OperatorAnalyticsOperatorServer[];
  sla_enabled: boolean | null;
  meta: OperatorAnalyticsMetaWithCoverageServer;
};

export type OperatorAnalyticsChannelOptionServer = {
  id: string;
  name: string;
  kind: string;
  deleted: boolean;
};

export type OperatorAnalyticsCountSeriesServer = {
  id: string;
  name: string;
  total: number;
  points: OperatorAnalyticsCountPointServer[];
};

export type OperatorAnalyticsChannelsServer = {
  granularity: OperatorAnalyticsGranularityServer;
  total: number;
  channels: OperatorAnalyticsChannelOptionServer[];
  series: OperatorAnalyticsCountSeriesServer[];
  meta: OperatorAnalyticsMetaServer;
};

export type OperatorAnalyticsTagRowServer = {
  id: string;
  name: string;
  new_tickets: number;
};

export type OperatorAnalyticsTagsServer = {
  granularity: OperatorAnalyticsGranularityServer;
  tags: OperatorAnalyticsTagRowServer[];
  series: OperatorAnalyticsCountSeriesServer[];
  meta: OperatorAnalyticsMetaServer;
};

export type OperatorAnalyticsQualityOperatorServer = {
  manager_id: string;
  name: string;
  deleted: boolean;
};

export type OperatorAnalyticsQualityItemServer = {
  ticket_id: number;
  conversation_id: number;
  first_message_id: string | null;
  date_closed: string;
  user_name: string;
  channel: OperatorAnalyticsChannelOptionServer | null;
  operator: OperatorAnalyticsQualityOperatorServer | null;
  rating: number | null;
  comment: string | null;
};

export type OperatorAnalyticsQualitySummaryServer = {
  enabled: boolean;
  average: number | null;
  rated_count: number;
  closed_count: number;
  distribution: Record<'1' | '2' | '3' | '4' | '5', number>;
};

export type OperatorAnalyticsQualityServer = {
  summary: OperatorAnalyticsQualitySummaryServer;
  items: OperatorAnalyticsQualityItemServer[];
  next_cursor: string | null;
  meta: OperatorAnalyticsMetaServer;
};
